/**
 * What a payment endpoint can actually hand back, written down once.
 *
 * The payment state carried twenty-five `any`, more than any other file in the
 * app, and it is the one place where a mistake costs money. They were not
 * laziness: the responses genuinely arrive in more than one shape, sometimes
 * wrapped in { value }, sometimes not, and sometimes with snake_case keys from
 * the gateway rather than camelCase from our own API. `any` was the quickest way
 * to stop the compiler asking.
 *
 * The cost was that nothing described the uncertainty, so it was re-derived by
 * hand at every call site and drifted. This file describes it instead.
 */

/** Every field a payment payload may carry, all optional because any of them may be absent. */
export interface PaymentPayload {
  transactionId?: string;
  id?: string;
  paymentIntentClientSecret?: string;
  clientSecret?: string;
  status?: string;
  paymentStatus?: string;
  customerId?: string;
  /** The gateway's own spelling. Present when the response comes straight from Stripe. */
  customer_id?: string;
  ephemeralKeySecret?: string;
  /** The gateway's own spelling, as above. */
  ephemeral_key_secret?: string;
  /** Set when the bank demands authentication, typically 3-D Secure. */
  requiresAction?: boolean;
  error?: unknown;
}

/** Our API's usual envelope. The payload may also arrive without it. */
export interface PaymentEnvelope {
  value?: PaymentPayload;
  error?: unknown;
}

/**
 * The payload, whether or not it arrived wrapped.
 *
 * This rule was written twice, identically, a few hundred lines apart. Two
 * copies of a rule about an uncertain shape is two places to forget when the
 * shape changes.
 */
export function unwrapPayment(
  response: PaymentEnvelope | PaymentPayload | null | undefined,
): PaymentPayload {
  if (!response) {
    return {};
  }

  const envelope = response as PaymentEnvelope;
  if (envelope.value) {
    return envelope.value;
  }

  return response as PaymentPayload;
}

/**
 * A message a person can read, out of whatever the API called an error.
 *
 * The same five lines appeared five times: a string is used as is, an object is
 * asked for its `message`, and anything else is stringified. Stringifying is a
 * poor last resort, but it beats showing nothing, and it is what the code
 * already did.
 */
export function paymentErrorMessage(error: unknown, fallback: string): string {
  if (typeof error === 'string' && error) {
    return error;
  }

  if (error && typeof error === 'object') {
    const message = (error as { message?: unknown }).message;
    if (typeof message === 'string' && message) {
      return message;
    }
    const serialised = JSON.stringify(error);
    if (serialised && serialised !== '{}') {
      return serialised;
    }
  }

  return fallback;
}

/** The customer identifier, under either spelling. */
export function customerIdOf(payload: PaymentPayload): string | undefined {
  return payload.customerId ?? payload.customer_id;
}

/** The ephemeral key, under either spelling. */
export function ephemeralKeyOf(payload: PaymentPayload): string | undefined {
  return payload.ephemeralKeySecret ?? payload.ephemeral_key_secret;
}
